import {
  Autocomplete,
  Box,
  Chip,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CloseButton } from '@/components/ds';
import {
  ALL_MARKETS,
  LANGUAGES,
  MORE_LANGUAGES,
  POPULAR_MARKETS,
  REFERRAL_SOURCES,
  YEARS_OF_EXPERIENCE,
} from '@/constants/profileOptions';
import { useAuthContext } from '@/context/AuthProvider';
import { useClientGroups, useCreateMember, useDeleteMember } from '@/hooks/useClientGroups';
import {
  useHousehold,
  useInviteHouseholdMember,
  useRemoveHouseholdMember,
} from '@/hooks/useHousehold';
import { useUserProfile } from '@/hooks/user/useUserProfile';
import { useUserRole } from '@/hooks/useUserRole';
import { getAvatarUploadUrl } from '@/services/api/users/api';
import { toProperCase } from '@/utils/properCase';

const C = {
  bg: 'var(--tx-fill-2)',
  card: 'var(--tx-glass-panel)',
  border: 'var(--tx-line)',
  borderLight: 'var(--tx-line)',
  text: 'var(--tx-text)',
  muted: 'var(--tx-muted)',
  subtle: 'var(--tx-faint)',
  lime: '#c0ff02',
  danger: '#ff4d4d',
  dangerDim: '#ff4d4d22',
};

const font = "'Manrope', sans-serif";

const EXPIRY_MONTHS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
const CURRENT_YEAR = new Date().getFullYear();
const EXPIRY_YEARS = Array.from({ length: 20 }, (_, i) => CURRENT_YEAR + i);

const dreFieldSx = {
  '& .MuiInputBase-root': {
    backgroundColor: 'var(--tx-fill)',
    border: 0,
    borderRadius: '10px',
    fontFamily: font,
    fontSize: 13,
    color: 'var(--tx-text)',
    height: 40,
    transition: 'border-color 0.15s',
    '&:hover': { borderColor: '#4a4a4a' },
    '&.Mui-focused': { borderColor: '#c0ff02' },
  },
  '& .MuiOutlinedInput-notchedOutline': { display: 'none' },
  '& .MuiInputBase-input': {
    padding: '0 4px 0 14px',
    fontSize: 13,
    fontFamily: font,
    color: 'var(--tx-text)',
  },
  '& .MuiInputBase-input::placeholder': {
    fontSize: 13,
    fontFamily: font,
    color: 'var(--tx-muted)',
    opacity: 1,
  },
};

const dreDropdownPaperSx = {
  // a menu surface is the solid popover (Tradehaus .tx-pop), never the 5% tile tint that lets the page show through
  backgroundColor: 'var(--tx-dialog)',
  backgroundImage: 'var(--tx-surface-light)',
  boxShadow: 'var(--tx-shadow-sm)',
  borderRadius: '12px',
  mt: '4px',
  '& .MuiAutocomplete-option': {
    fontFamily: font,
    fontSize: 13,
    color: 'var(--tx-text)',
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
    '&[aria-selected="true"]': { backgroundColor: 'rgba(187,183,175,0.15)' },
  },
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'var(--tx-fill)',
  border: 0,
  borderRadius: 10,
  padding: '10px 14px',
  color: C.text,
  fontSize: 13,
  fontFamily: font,
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s',
};

// the ds button recipe: 40px, radius 10, 600 at -0.02em; callers pick lime (the one save action) or the tonal fill
const btnPrimary: React.CSSProperties = {
  background: 'var(--tx-lime)',
  color: 'var(--tx-on-lime)',
  border: 'none',
  borderRadius: 10,
  height: 40,
  padding: '0 24px',
  fontSize: 14,
  fontWeight: 600,
  fontFamily: font,
  letterSpacing: '-0.02em',
  cursor: 'pointer',
  transition: 'all 0.2s',
};

const Toggle = ({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) => (
  <div
    onClick={() => onChange(!checked)}
    style={{
      width: 44,
      height: 24,
      borderRadius: '12px',
      background: checked ? C.lime : 'var(--tx-fill)',
      border: 0,
      cursor: 'pointer',
      position: 'relative',
      transition: 'all 0.2s',
      flexShrink: 0,
    }}>
    <div
      style={{
        position: 'absolute',
        top: 3,
        left: checked ? 22 : 3,
        width: 16,
        height: 16,
        borderRadius: '50%',
        background: checked ? 'var(--tx-glass-panel)' : C.subtle,
        transition: 'left 0.2s',
      }}
    />
  </div>
);

const Radio = ({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) => (
  <div
    onClick={onChange}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      cursor: 'pointer',
      padding: '10px 0',
    }}>
    <div
      style={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        border: `2px solid ${checked ? C.lime : C.subtle}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transition: 'border-color 0.15s',
      }}>
      {checked && <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.lime }} />}
    </div>
    <span
      style={{
        fontSize: 14,
        color: checked ? C.text : C.muted,
        fontFamily: font,
        fontWeight: checked ? 500 : 400,
      }}>
      {label}
    </span>
  </div>
);

const SectionHeader = ({
  title,
  subtitle,
  mobile,
}: {
  title: string;
  subtitle?: string;
  mobile?: boolean;
}) => (
  <div style={{ marginBottom: mobile ? 34 : 24 }}>
    <h2
      style={{
        margin: 0,
        fontSize: 17,
        fontWeight: 400,
        color: C.text,
        fontFamily: font,
        letterSpacing: '-0.015em',
      }}>
      {title}
    </h2>
    {subtitle && (
      <p style={{ margin: '6px 0 0 0', fontSize: 12, fontWeight: 500, color: C.muted, fontFamily: font }}>
        {subtitle}
      </p>
    )}
  </div>
);

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <label
    style={{
      fontSize: 12,
      fontWeight: 500,
      color: C.muted,
      fontFamily: font,
      display: 'block',
      marginBottom: 6,
    }}>
    {children}
  </label>
);

const useIsMobile = () => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down('md'));
};

const ProfileTab = () => {
  const mobile = useIsMobile();
  const { user } = useAuthContext();
  const { userProfile, updateUserProfile, isUpdatingUserProfile } = useUserProfile();
  const { isAdvisor, canAccessAdmin } = useUserRole();
  const [firstName, setFirstName] = useState(
    userProfile?.firstName || user?.name?.split(' ')[0] || '',
  );
  const [lastName, setLastName] = useState(
    userProfile?.lastName || user?.name?.split(' ').slice(1).join(' ') || '',
  );
  const [calLinkFocused, setCalLinkFocused] = useState(false);
  const [calLink, setCalLink] = useState(() =>
    (userProfile?.calLink || '')
      .replace(/^https?:\/\/(www\.)?cal\.com\//i, '')
      .replace(/^(www\.)?cal\.com\//i, '')
      .replace(/\/+$/, ''),
  );
  const [phone, setPhone] = useState(userProfile?.phoneNumber?.toString() || '');
  const email = user?.email || '';
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwSaved, setPwSaved] = useState(false);
  const [pwError, setPwError] = useState('');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initialize avatarPreview from profile.photoUrl on first load only — don't
  // overwrite a locally selected file/preview the user has already chosen.
  // Append updatedAt as a version param so the browser re-fetches when the profile changes,
  // since the S3 key is stable (same URL per user across uploads).
  useEffect(() => {
    if (userProfile?.photoUrl && !avatarFile && !avatarPreview) {
      setAvatarPreview(`${userProfile.photoUrl}?v=${userProfile.updatedAt}`);
    }
  }, [userProfile?.photoUrl]); // avatarFile/avatarPreview intentionally omitted — init-only

  // Revoke blob URLs to avoid memory leaks — covers both picking a new file and navigating away.
  useEffect(() => {
    const preview = avatarPreview;
    return () => {
      if (preview?.startsWith('blob:')) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [avatarPreview]);

  const displayName = [firstName, lastName].filter(Boolean).join(' ') || 'User';

  const handleAvatarClick = () => fileInputRef.current?.click();
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const mimeType = file.type;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(mimeType)) {
      setSaveError('Only JPEG, PNG, or WebP images are supported.');
      e.target.value = ''; // reset so the same file can trigger onChange if reselected
      return;
    }
    // Previous blob URL is revoked by the useEffect cleanup when avatarPreview changes
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
    e.target.value = ''; // reset so the same valid file can be reselected if needed
  };

  const handleSave = async () => {
    setSaveError('');
    try {
      let photoUrl: string | undefined;

      // Upload avatar if a new file was selected
      let uploadedPublicUrl: string | undefined;
      if (avatarFile) {
        // avatarFile.type was validated in handleFileChange; cast is safe
        const mimeType = avatarFile.type as 'image/jpeg' | 'image/png' | 'image/webp';
        const { presignedUrl, publicUrl } = await getAvatarUploadUrl(mimeType);
        const uploadRes = await fetch(presignedUrl, {
          method: 'PUT',
          body: avatarFile,
          headers: { 'Content-Type': mimeType },
        });
        if (!uploadRes.ok) throw new Error('Avatar upload failed');
        // Store the stable URL (no query params) — cache-busting happens at render time via updatedAt
        photoUrl = publicUrl;
        uploadedPublicUrl = publicUrl;
      }

      const calData = isAdvisor || canAccessAdmin ? { calLink: calLink || undefined } : {};
      await updateUserProfile({
        firstName,
        lastName,
        phoneNumber: phone ? Number(phone) : undefined,
        ...calData,
        ...(photoUrl ? { photoUrl } : {}),
      });
      setAvatarFile(null); // clear pending file
      // Show the fresh image immediately in this session; the useEffect cleanup
      // will revoke the old blob URL when avatarPreview changes.
      if (uploadedPublicUrl) {
        setAvatarPreview(`${uploadedPublicUrl}?v=${Date.now()}`);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setSaveError('Failed to update profile. Please try again.');
    }
  };

  const handlePwChange = () => {
    setPwError('');
    if (!currentPw) {
      setPwError('Enter your current password.');
      return;
    }
    if (newPw.length < 8) {
      setPwError('New password must be at least 8 characters.');
      return;
    }
    if (newPw !== confirmPw) {
      setPwError("Passwords don't match.");
      return;
    }
    setPwSaved(true);
    setCurrentPw('');
    setNewPw('');
    setConfirmPw('');
    setTimeout(() => setPwSaved(false), 2500);
  };

  const pwStrength =
    newPw.length === 0
      ? null
      : newPw.length < 8
      ? 'weak'
      : newPw.match(/[A-Z]/) && newPw.match(/[0-9]/)
      ? 'strong'
      : 'medium';

  const getInitials = (n: string) =>
    n
      .split(' ')
      .filter(Boolean)
      .map((p) => p[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

  return (
    <div>
      <SectionHeader title="Profile" mobile={mobile} />

      {/* Avatar */}
      <input
        data-testid="avatar-file-input"
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
        <div
          data-testid="avatar-upload"
          onClick={handleAvatarClick}
          className="avatar-upload"
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: 'var(--tx-fill-2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 22,
            fontWeight: 600,
            color: 'var(--tx-text)',
            fontFamily: font,
            flexShrink: 0,
            cursor: 'pointer',
            overflow: 'hidden',
            position: 'relative',
          }}>
          {avatarPreview ? (
            <img
              data-testid="avatar-image"
              src={avatarPreview}
              alt="Avatar"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <span data-testid="avatar-initials">{getInitials(displayName)}</span>
          )}
          <div
            className="avatar-overlay"
            style={{
              position: 'absolute',
              inset: 0,
              background: 'var(--tx-backdrop)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0,
              transition: 'opacity 0.2s',
              borderRadius: '50%',
            }}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
          </div>
          <style>{`.avatar-upload:hover .avatar-overlay { opacity: 1 !important; }`}</style>
        </div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.text, fontFamily: font }}>
            {displayName}
          </div>
          <div style={{ fontSize: 12, color: C.muted, fontFamily: font, marginTop: 2 }}>
            {email}
          </div>
        </div>
      </div>

      {/* Name + Email fields */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          maxWidth: mobile ? undefined : 560,
        }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <FieldLabel>First name</FieldLabel>
            <input
              value={firstName}
              onChange={(e) => setFirstName(toProperCase(e.target.value))}
              style={inputStyle}
              onFocus={(e) => {
                e.target.style.borderColor = C.lime;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = C.border;
              }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <FieldLabel>Last name</FieldLabel>
            <input
              value={lastName}
              onChange={(e) => setLastName(toProperCase(e.target.value))}
              style={inputStyle}
              onFocus={(e) => {
                e.target.style.borderColor = C.lime;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = C.border;
              }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <FieldLabel>Email</FieldLabel>
            <input
              value={email}
              disabled
              style={{ ...inputStyle, opacity: 0.5, cursor: 'not-allowed' }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <FieldLabel>Phone</FieldLabel>
            <input
              value={
                phone.length === 0
                  ? ''
                  : phone.length <= 3
                  ? `(${phone}`
                  : phone.length <= 6
                  ? `(${phone.slice(0, 3)}) ${phone.slice(3)}`
                  : `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`
              }
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
              placeholder="(555) 123-4567"
              style={inputStyle}
              onFocus={(e) => {
                e.target.style.borderColor = C.lime;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = C.border;
              }}
            />
          </div>
        </div>
        {(isAdvisor || canAccessAdmin) && (
          <div>
            <FieldLabel>Scheduling link</FieldLabel>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                background: 'var(--tx-fill)',
                border: 0,
                boxShadow: calLinkFocused ? `inset 0 0 0 1px ${C.lime}` : 'none',
                borderRadius: 10,
                overflow: 'hidden',
                transition: 'border-color 0.15s',
                boxSizing: 'border-box',
              }}>
              <span
                style={{
                  color: C.subtle,
                  fontSize: 13,
                  fontFamily: font,
                  paddingLeft: 14,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}>
                cal.com/
              </span>
              <input
                value={calLink}
                onChange={(e) => {
                  const v = e.target.value
                    .replace(/^https?:\/\/(www\.)?cal\.com\//i, '')
                    .replace(/^(www\.)?cal\.com\//i, '')
                    .replace(/\/+$/, '');
                  setCalLink(v);
                }}
                placeholder="username/60min"
                style={{
                  ...inputStyle,
                  border: 'none',
                  borderRadius: 0,
                  paddingLeft: 4,
                  flex: 1,
                  background: 'transparent',
                  width: '100%',
                }}
                onFocus={() => setCalLinkFocused(true)}
                onBlur={() => setCalLinkFocused(false)}
              />
            </div>
          </div>
        )}
      </div>

      <button
        onClick={handleSave}
        disabled={isUpdatingUserProfile}
        style={{
          ...btnPrimary,
          width: mobile ? '100%' : 180,
          marginTop: 24,
          ...(saved ? { background: 'var(--tx-fill)', color: C.muted } : null),
          opacity: isUpdatingUserProfile ? 0.6 : 1,
          cursor: isUpdatingUserProfile ? 'not-allowed' : 'pointer',
        }}>
        {isUpdatingUserProfile ? 'Saving…' : saved ? '\u2713 Saved' : 'Save changes'}
      </button>
      {saveError && (
        <div style={{ fontSize: 12, color: C.danger, fontFamily: font, marginTop: 8 }}>
          {saveError}
        </div>
      )}

      {/* Divider */}
      <div style={{ height: 1, background: C.border, margin: '32px 0 28px', maxWidth: mobile ? undefined : 560 }} />

      {/* Change Password */}
      <div style={{ maxWidth: mobile ? undefined : 560 }}>
        <SectionHeader title="Password" subtitle="At least 8 characters." mobile={mobile} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {[
            {
              label: 'Current password',
              val: currentPw,
              set: setCurrentPw,
              placeholder: '••••••••',
            },
            { label: 'New password', val: newPw, set: setNewPw, placeholder: 'Min. 8 characters' },
            {
              label: 'Confirm new password',
              val: confirmPw,
              set: setConfirmPw,
              placeholder: 'Re-enter new password',
            },
          ].map(({ label, val, set, placeholder }) => (
            <div key={label}>
              <FieldLabel>{label}</FieldLabel>
              <input
                type="password"
                value={val}
                onChange={(e) => {
                  set(e.target.value);
                  setPwError('');
                }}
                placeholder={placeholder}
                style={{ ...inputStyle, letterSpacing: val ? '0.1em' : 'normal' }}
                onFocus={(e) => {
                  e.target.style.borderColor = C.lime;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = C.border;
                }}
              />
            </div>
          ))}
        </div>

        {pwStrength && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
            {(['weak', 'medium', 'strong'] as const).map((level, i) => (
              <div
                key={level}
                style={{
                  height: 3,
                  flex: 1,
                  borderRadius: 2,
                  background:
                    pwStrength === 'weak' && i === 0
                      ? '#ff4d4d'
                      : pwStrength === 'medium' && i <= 1
                      ? '#f0a500'
                      : pwStrength === 'strong'
                      ? C.lime
                      : C.border,
                  transition: 'background 0.2s',
                }}
              />
            ))}
            <span
              style={{
                fontSize: 11,
                fontFamily: font,
                fontWeight: 600,
                color:
                  pwStrength === 'weak' ? '#ff4d4d' : pwStrength === 'medium' ? '#f0a500' : C.lime,
                whiteSpace: 'nowrap',
              }}>
              {pwStrength}
            </span>
          </div>
        )}

        {pwError && (
          <div style={{ fontSize: 12, color: C.danger, fontFamily: font, marginTop: 10 }}>
            {pwError}
          </div>
        )}

        <button
          onClick={handlePwChange}
          style={{
            ...btnPrimary,
            width: mobile ? '100%' : 180,
            marginTop: 26,
            // tonal: the lime save above stays the screen's one primary action
            background: 'var(--tx-fill)',
            color: pwSaved ? C.muted : 'var(--tx-text)',
          }}>
          {pwSaved ? '\u2713 Password updated' : 'Update password'}
        </button>
      </div>

      {/* Delete account */}
      <div style={{ height: 1, background: C.border, margin: '32px 0 24px', maxWidth: mobile ? undefined : 560 }} />
      <div>
        <div
          style={{
            width: mobile ? '100%' : 320,
            display: 'flex',
            flexDirection: 'column-reverse',
          }}>
          <div style={{ width: '100%' }}>
            <div
              onClick={() => {
                setShowDelete(!showDelete);
                setDeleteConfirm('');
              }}
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: C.danger,
                fontFamily: font,
                cursor: 'pointer',
              }}>
              Delete account
            </div>
            {!showDelete && (
              <p
                style={{
                  fontSize: 12,
                  color: C.subtle,
                  fontFamily: font,
                  margin: '5px 0 0 0',
                  lineHeight: 1.5,
                  maxWidth: 320,
                }}>
                Permanently delete your account. You'll lose access to all property reports, scores,
                and your dashboard.
              </p>
            )}
          </div>
          {showDelete && (
            <div style={{ width: '100%', marginBottom: 10 }}>
              <p
                style={{
                  fontSize: 13,
                  color: C.muted,
                  fontFamily: font,
                  margin: '0 0 10px 0',
                  lineHeight: 1.5,
                }}>
                This action is permanent. Type{' '}
                <span style={{ fontWeight: 600, color: C.text }}>Delete My Account</span>{' '}
                (case-sensitive) to confirm.
              </p>
              <input
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder="Delete My Account"
                style={{ ...inputStyle, marginBottom: 10 }}
                onFocus={(e) => {
                  e.target.style.borderColor = C.danger;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = C.border;
                }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  disabled={deleteConfirm !== 'Delete My Account'}
                  style={{
                    background: deleteConfirm === 'Delete My Account' ? C.danger : 'var(--tx-fill)',
                    color: deleteConfirm === 'Delete My Account' ? 'var(--tx-text)' : C.subtle,
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 16px',
                    fontSize: 13,
                    fontWeight: 600,
                    fontFamily: font,
                    cursor: deleteConfirm === 'Delete My Account' ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s',
                  }}>
                  Confirm Delete
                </button>
                <button
                  onClick={() => {
                    setShowDelete(false);
                    setDeleteConfirm('');
                  }}
                  style={{
                    background: 'var(--tx-fill)',
                    color: C.muted,
                    border: 0,
                    borderRadius: 8,
                    padding: '8px 16px',
                    fontSize: 13,
                    fontFamily: font,
                    cursor: 'pointer',
                  }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MEMBER_ROLES_BUYER = ['Co-Buyer', 'Counsel', 'Advisor', 'Other'];

const memberInputSx = {
  '& .MuiInputBase-root': {
    backgroundColor: 'var(--tx-fill)',
    border: 0,
    borderRadius: '10px',
    fontFamily: font,
    fontSize: 13,
    color: 'var(--tx-text)',
    '&:hover': { borderColor: '#4a4a4a' },
    '&.Mui-focused': { borderColor: '#c0ff02' },
  },
  '& .MuiOutlinedInput-notchedOutline': { display: 'none' },
  '& .MuiInputBase-input': {
    fontSize: 13,
    fontFamily: font,
    color: 'var(--tx-text)',
    padding: '10px 14px',
  },
  '& .MuiInputBase-input::placeholder': {
    color: 'var(--tx-faint)',
    opacity: 1,
    fontSize: 13,
    fontFamily: font,
  },
};

const emptyMemberForm = { name: '', email: '', phone: '', role: 'Co-Buyer' };

const ClientGroupUsersTab = () => {
  const mobile = useIsMobile();
  const { user } = useAuthContext();
  const { groups, isLoading } = useClientGroups();
  const createMemberMutation = useCreateMember();
  const deleteMemberMutation = useDeleteMember();

  const group = groups[0];
  const members = group?.members ?? [];

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyMemberForm);
  const [formError, setFormError] = useState('');

  const handleAdd = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      setFormError('Full name and email are required.');
      return;
    }
    if (!group) return;
    try {
      await createMemberMutation.mutateAsync({
        groupId: group.id,
        data: {
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          role: form.role,
        },
      });
      setForm(emptyMemberForm);
      setShowForm(false);
      setFormError('');
    } catch {
      setFormError('Failed to send invite. Please try again.');
    }
  };

  const handleDelete = async (memberId: string) => {
    await deleteMemberMutation.mutateAsync(memberId);
  };

  const getInitials = (name: string) => {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    return parts[0]?.slice(0, 2).toUpperCase() ?? '?';
  };

  return (
    <div>
      <SectionHeader
        title="Manage Users"
        subtitle="Invite co-buyers, advisors, or counsel into your group."
        mobile={mobile}
      />

      <div style={{ maxWidth: mobile ? undefined : 560 }}>
        {/* Member list */}
        {isLoading ? (
          <div style={{ fontSize: 13, color: C.subtle, fontFamily: font, padding: '16px 0' }}>
            Loading...
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
            {members.length === 0 && !showForm && (
              <div
                style={{
                  textAlign: 'center',
                  padding: '32px 0',
                  color: C.subtle,
                  fontSize: 13,
                  fontFamily: font,
                }}>
                No members yet. Add a co-buyer, advisor, or counsel below.
              </div>
            )}
            {members.map((m) => {
              const initials = getInitials(m.name);
              return (
                <div
                  key={m.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 16px',
                    background: 'rgba(var(--tx-ink-rgb), 0.05)',
                    borderRadius: '12px',
                  }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: 'var(--tx-fill-2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--tx-text)',
                      fontFamily: font,
                      flexShrink: 0,
                      overflow: 'hidden',
                    }}>
                    {m.photoUrl ? (
                      <img
                        src={m.photoUrl}
                        alt={m.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      initials
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.text, fontFamily: font }}>
                      {m.name}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: C.muted,
                        fontFamily: font,
                        marginTop: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                      {m.email}
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: C.muted,
                      fontFamily: font,
                      padding: '4px 10px',
                      border: 0,
                      background: 'var(--tx-fill)',
                      borderRadius: 6,
                      whiteSpace: 'nowrap',
                    }}>
                    {m.role}
                  </div>
                  {m.userId !== user?.id && (
                    <button
                      onClick={() => handleDelete(m.id)}
                      disabled={deleteMemberMutation.isPending}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: C.subtle,
                        cursor: 'pointer',
                        fontSize: 17,
                        padding: '0 4px',
                        lineHeight: 1,
                      }}>
                      &times;
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Add member form */}
        {showForm && (
          <div
            style={{
              background: 'rgba(var(--tx-ink-rgb), 0.05)',
              borderRadius: '12px',
              padding: 16,
              marginBottom: 10,
            }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: C.muted,
                fontFamily: font,
                marginBottom: 12,
              }}>
              New user
            </div>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Box>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: C.muted,
                    fontFamily: font,
                    marginBottom: 4,
                  }}>
                  Full name <span style={{ color: '#DB5282' }}>*</span>
                </div>
                <TextField
                  size="small"
                  placeholder="Full name"
                  fullWidth
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  sx={memberInputSx}
                />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <Box>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: C.muted,
                      fontFamily: font,
                      marginBottom: 4,
                    }}>
                    Email <span style={{ color: '#DB5282' }}>*</span>
                  </div>
                  <TextField
                    size="small"
                    placeholder="Email"
                    fullWidth
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    sx={memberInputSx}
                  />
                </Box>
                <Box>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: C.muted,
                      fontFamily: font,
                      marginBottom: 4,
                    }}>
                    Phone
                  </div>
                  <TextField
                    size="small"
                    placeholder="Phone"
                    fullWidth
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    sx={memberInputSx}
                  />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', mt: '2px' }}>
                {MEMBER_ROLES_BUYER.map((r) => (
                  <Box
                    component="button"
                    key={r}
                    onClick={() => setForm((f) => ({ ...f, role: r }))}
                    sx={{
                      padding: '4px 10px',
                      borderRadius: '6px',
                      fontSize: 11,
                      fontWeight: 600,
                      fontFamily: font,
                      cursor: 'pointer',
                      border: 0,
                      background: form.role === r ? 'var(--tx-fill-2)' : 'var(--tx-fill)',
                      color: form.role === r ? 'var(--tx-text)' : 'var(--tx-muted)',
                    }}>
                    {r}
                  </Box>
                ))}
              </Box>
            </Box>
            {formError && (
              <div style={{ fontSize: 12, color: C.danger, fontFamily: font, marginTop: 8 }}>
                {formError}
              </div>
            )}
            {(() => {
              const canSubmit = form.name.trim().length > 0 && form.email.trim().length > 0;
              return (
                <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      setShowForm(false);
                      setForm(emptyMemberForm);
                      setFormError('');
                    }}
                    style={{
                      ...btnPrimary,
                      background: 'var(--tx-fill)',
                      color: C.muted,
                      border: 0,
                    }}>
                    Cancel
                  </button>
                  <button
                    onClick={handleAdd}
                    disabled={!canSubmit || createMemberMutation.isPending}
                    style={{
                      ...btnPrimary,
                      ...(!canSubmit
                        ? {
                            background: 'var(--tx-fill)',
                            color: C.subtle,
                            cursor: 'not-allowed',
                            border: 0,
                          }
                        : {}),
                    }}>
                    {createMemberMutation.isPending ? 'Sending...' : 'Send Invite'}
                  </button>
                </div>
              );
            })()}
          </div>
        )}

        {/* Add member trigger */}
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            style={{
              width: '100%',
              background: 'transparent',
              border: `1px dashed ${C.border}`,
              borderRadius: 10,
              padding: '12px 0',
              color: C.muted,
              fontSize: 13,
              fontFamily: font,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}>
            <span style={{ fontSize: 15, lineHeight: 1 }}>+</span> Invite User
          </button>
        )}

        {members.length > 0 && (
          <p style={{ fontSize: 12, color: C.subtle, fontFamily: font, marginTop: 16 }}>
            {members.length} member{members.length !== 1 ? 's' : ''} in your group.
          </p>
        )}
      </div>
    </div>
  );
};

/**
 * Manage Users serves two audiences: people in an advisor-led client group
 * keep the group tool (co-buyers, counsel), and HomeHQ homeowners get
 * household sharing with their plan's seat limit.
 */
const ManageUsersTab = () => {
  const { groups, isLoading } = useClientGroups();
  if (isLoading) {
    return (
      <div style={{ fontSize: 13, color: C.subtle, fontFamily: font, padding: '16px 0' }}>
        Loading...
      </div>
    );
  }
  return groups.length > 0 ? <ClientGroupUsersTab /> : <HouseholdUsersTab />;
};

const emptyHouseholdForm = { name: '', email: '' };

const householdBadgeStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: C.muted,
  fontFamily: font,
  padding: '4px 10px',
  border: 0,
  background: 'var(--tx-fill)',
  borderRadius: 6,
  whiteSpace: 'nowrap',
};

const HouseholdPersonRow = ({
  name,
  email,
  badge,
  onRemove,
  removeLabel,
  disabled,
}: {
  name: string;
  email: string;
  badge: string;
  onRemove?: () => void;
  removeLabel?: string;
  disabled?: boolean;
}) => {
  const initials =
    name
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || '?';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        background: 'rgba(var(--tx-ink-rgb), 0.05)',
        borderRadius: '12px',
      }}>
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: 'var(--tx-fill-2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--tx-text)',
          fontFamily: font,
          flexShrink: 0,
        }}>
        {initials}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: C.text, fontFamily: font }}>
          {name || email}
        </div>
        <div
          style={{
            fontSize: 12,
            color: C.muted,
            fontFamily: font,
            marginTop: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
          {email}
        </div>
      </div>
      <div style={householdBadgeStyle}>{badge}</div>
      {onRemove && (
        <button
          onClick={onRemove}
          disabled={disabled}
          aria-label={removeLabel}
          title={removeLabel}
          style={{
            background: 'transparent',
            border: 'none',
            color: C.subtle,
            cursor: 'pointer',
            fontSize: 17,
            padding: '0 4px',
            lineHeight: 1,
          }}>
          &times;
        </button>
      )}
    </div>
  );
};

export const HouseholdUsersTab = () => {
  const mobile = useIsMobile();
  const { user } = useAuthContext();
  const { data: household, isLoading } = useHousehold();
  const inviteMutation = useInviteHouseholdMember();
  const removeMutation = useRemoveHouseholdMember();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyHouseholdForm);
  const [formError, setFormError] = useState('');

  if (isLoading || !household) {
    return (
      <div>
        <SectionHeader title="Manage Users" subtitle="" mobile={mobile} />
        <div style={{ fontSize: 13, color: C.subtle, fontFamily: font, padding: '16px 0' }}>
          {isLoading ? 'Loading...' : 'Unable to load your household.'}
        </div>
      </div>
    );
  }

  const isOwner = household.role === 'owner';
  const { limit, used } = household.seats;
  const seatsFull = limit !== null && used >= limit;
  const ownerName = household.owner?.name || household.owner?.email || 'the homeowner';

  const subtitle =
    household.role === 'owner'
      ? 'Share your Home HQ with your household. Everyone you add gets full access to your home.'
      : household.role === 'member'
      ? `You have access to ${ownerName}'s Home HQ.`
      : 'Once your home is set up, you can share your Home HQ with your household here.';

  const handleInvite = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      setFormError('Full name and email are required.');
      return;
    }
    try {
      await inviteMutation.mutateAsync({ name: form.name.trim(), email: form.email.trim() });
      setForm(emptyHouseholdForm);
      setShowForm(false);
      setFormError('');
    } catch (err: any) {
      setFormError(err?.response?.data?.message ?? 'Failed to send invite. Please try again.');
    }
  };

  return (
    <div>
      <SectionHeader title="Manage Users" subtitle={subtitle} mobile={mobile} />

      {household.role !== 'none' && (
        <div style={{ maxWidth: mobile ? undefined : 560 }}>
          {/* Seats */}
          <div style={{ marginBottom: 16 }} data-testid="household-seats">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 13,
                fontFamily: font,
                color: C.text,
                marginBottom: 6,
              }}>
              <span style={{ fontWeight: 600 }}>
                {limit === null ? `${used} seats used` : `${used} of ${limit} seats used`}
              </span>
              <span style={{ color: C.subtle }}>The homeowner counts as one seat</span>
            </div>
            {limit !== null && (
              <div style={{ height: 6, borderRadius: 3, background: C.border, overflow: 'hidden' }}>
                <div
                  style={{
                    width: `${Math.min(100, (used / Math.max(limit, 1)) * 100)}%`,
                    height: '100%',
                    background: seatsFull ? C.muted : C.lime,
                  }}
                />
              </div>
            )}
          </div>

          {/* People */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
            {household.owner && (
              <HouseholdPersonRow
                name={household.owner.name}
                email={household.owner.email}
                badge="Owner"
              />
            )}
            {household.members.map((m) => {
              const isMe =
                !isOwner && !!user?.email && m.email.toLowerCase() === user.email.toLowerCase();
              return (
                <HouseholdPersonRow
                  key={m.id}
                  name={m.name}
                  email={m.email}
                  badge={isMe ? 'You' : m.status === 'invited' ? 'Invited' : 'Member'}
                  disabled={removeMutation.isPending}
                  removeLabel={`Remove ${m.name || m.email}`}
                  // Only the owner manages the household.
                  onRemove={isOwner ? () => removeMutation.mutate(m.id) : undefined}
                />
              );
            })}
          </div>

          {/* Invite */}
          {isOwner && showForm && (
            <div
              style={{
                background: 'rgba(var(--tx-ink-rgb), 0.05)',
                borderRadius: '12px',
                padding: 16,
                marginBottom: 10,
              }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap: '8px' }}>
                <Box>
                  <FieldLabel>Full name</FieldLabel>
                  <TextField
                    size="small"
                    placeholder="Full name"
                    fullWidth
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    sx={memberInputSx}
                  />
                </Box>
                <Box>
                  <FieldLabel>Email</FieldLabel>
                  <TextField
                    size="small"
                    placeholder="Email"
                    fullWidth
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    sx={memberInputSx}
                  />
                </Box>
              </Box>
              {formError && (
                <div style={{ fontSize: 12, color: C.danger, fontFamily: font, marginTop: 8 }}>
                  {formError}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowForm(false);
                    setForm(emptyHouseholdForm);
                    setFormError('');
                  }}
                  style={{
                    ...btnPrimary,
                    background: 'var(--tx-fill)',
                    color: C.muted,
                    border: 0,
                  }}>
                  Cancel
                </button>
                <button
                  onClick={handleInvite}
                  disabled={inviteMutation.isPending}
                  style={btnPrimary}>
                  {inviteMutation.isPending ? 'Sending...' : 'Send Invite'}
                </button>
              </div>
            </div>
          )}

          {isOwner && !showForm && household.canInvite && (
            <button
              onClick={() => setShowForm(true)}
              style={{
                width: '100%',
                background: 'transparent',
                border: `1px dashed ${C.border}`,
                borderRadius: 10,
                padding: '12px 0',
                color: C.muted,
                fontSize: 13,
                fontFamily: font,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}>
              <span style={{ fontSize: 15, lineHeight: 1 }}>+</span> Invite to your household
            </button>
          )}

          {isOwner && !household.canInvite && (
            <div
              style={{
                fontSize: 13,
                color: C.muted,
                fontFamily: font,
                padding: '12px 16px',
                border: `1px dashed ${C.border}`,
                borderRadius: 10,
              }}>
              {seatsFull
                ? 'All seats are in use. Upgrade your plan or remove someone to invite more people.'
                : 'An active Home HQ plan is required to share your home.'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const _NotificationsTab = () => {
  const mobile = useIsMobile();
  const {
    userProfile,
    updateNotificationPreferences,
    isUpdatingUserProfile,
    updateUserProfileError,
  } = useUserProfile();

  const [notify, setNotify] = useState<'all' | 'direct' | 'none'>('direct');
  const [commEmail, setCommEmail] = useState(false);
  const [mktEmail, setMktEmail] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!userProfile) return;
    setNotify(userProfile.notificationFrequency ?? 'direct');
    setCommEmail(userProfile.commEmailEnabled ?? false);
    setMktEmail(userProfile.marketingOptIn ?? false);
  }, [userProfile]);

  const handleSave = async () => {
    const success = await updateNotificationPreferences({
      notificationFrequency: notify,
      commEmailEnabled: commEmail,
      marketingOptIn: mktEmail,
    });
    if (success) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    }
  };

  const emailItems = [
    {
      key: 'comm',
      label: 'Communication emails',
      sub: 'Send notifications about activity on your properties.',
      val: commEmail,
      set: setCommEmail,
    },
    {
      key: 'mkt',
      label: 'Marketing emails',
      sub: 'Receive updates about new features, tools, and platform news.',
      val: mktEmail,
      set: setMktEmail,
    },
  ];

  return (
    <div>
      <SectionHeader
        title="Notifications"
        subtitle="Configure how you receive updates and alerts."
        mobile={mobile}
      />

      <div style={{ marginBottom: 32 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: C.muted,
            fontFamily: font,
            marginBottom: 4,
          }}>
          Notify me about...
        </div>
        <div style={{ maxWidth: mobile ? undefined : 560 }}>
          <Radio
            checked={notify === 'all'}
            onChange={() => setNotify('all')}
            label="All new messages"
          />
          <Radio
            checked={notify === 'direct'}
            onChange={() => setNotify('direct')}
            label="Direct messages and mentions"
          />
          <Radio checked={notify === 'none'} onChange={() => setNotify('none')} label="Nothing" />
        </div>
      </div>

      <div>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: C.muted,
            fontFamily: font,
            marginBottom: 12,
          }}>
          Email notifications
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            maxWidth: mobile ? undefined : 560,
          }}>
          {emailItems.map((item) => (
            <div
              key={item.key}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 16px',
                background: 'rgba(var(--tx-ink-rgb), 0.05)',
                borderRadius: '12px',
                transition: 'border-color 0.2s',
              }}>
              <div style={{ flex: 1, paddingRight: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.text, fontFamily: font }}>
                  {item.label}
                </div>
                <div style={{ fontSize: 12, color: C.muted, fontFamily: font, marginTop: 2 }}>
                  {item.sub}
                </div>
              </div>
              <Toggle checked={item.val} onChange={item.set} />
            </div>
          ))}
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={isUpdatingUserProfile}
        style={{
          ...btnPrimary,
          marginTop: 28,
          background: saved ? C.card : 'var(--tx-fill-2)',
          color: saved ? C.muted : 'var(--tx-text)',
          opacity: isUpdatingUserProfile ? 0.6 : 1,
          cursor: isUpdatingUserProfile ? 'not-allowed' : 'pointer',
        }}>
        {isUpdatingUserProfile ? 'Saving...' : saved ? '\u2713 Saved' : 'Update Notifications'}
      </button>
      {updateUserProfileError && (
        <div style={{ fontSize: 12, color: C.danger, fontFamily: font, marginTop: 8 }}>
          {updateUserProfileError}
        </div>
      )}
    </div>
  );
};

// ── Advisor-specific constants (imported from @/constants/profileOptions) ──────
const selectStyle: React.CSSProperties = {
  ...inputStyle,
  appearance: 'none',
  WebkitAppearance: 'none',
  cursor: 'pointer',
  // eslint-disable-next-line max-len
  background: `#333333 url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23bbb7af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E") no-repeat right 12px center`,
};

const muiInputSx = {
  '& .MuiInputBase-root': {
    backgroundColor: 'var(--tx-fill)',
    borderRadius: '10px',
    fontFamily: font,
    fontSize: 13,
    color: C.text,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: C.border,
    borderRadius: '10px',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: C.borderLight,
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: C.lime,
    borderWidth: 1,
  },
  '& .MuiInputBase-input': {
    fontSize: 13,
    fontFamily: font,
    color: C.text,
    padding: '10px 14px',
  },
  '& .MuiInputBase-input::placeholder': {
    fontSize: 13,
    fontFamily: font,
    color: C.muted,
    opacity: 1,
  },
};

// ── Advisor Tab ────────────────────────────────────────────────────────────

const AdvisorTab = () => {
  const mobile = useIsMobile();
  const { userProfile, updateUserProfile } = useUserProfile();
  const [dreNumber, setDreNumber] = useState('');
  const [dreExpiryMonth, setDreExpiryMonth] = useState('');
  const [dreExpiryDay, setDreExpiryDay] = useState('');
  const [dreExpiryYear, setDreExpiryYear] = useState('');
  const [yearsExperience, setYearsExperience] = useState('');
  const [markets, setMarkets] = useState<string[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [referralSource, setReferralSource] = useState('');
  const [notes, setNotes] = useState('');
  const [saved, setSaved] = useState(false);

  // Initialize from persisted profile
  useEffect(() => {
    if (!userProfile) return;
    if (userProfile.dreNumber) setDreNumber(userProfile.dreNumber);
    if (userProfile.dreExpiry) {
      setDreExpiryMonth(userProfile.dreExpiry.slice(5, 7));
      setDreExpiryDay(userProfile.dreExpiry.slice(8, 10));
      setDreExpiryYear(userProfile.dreExpiry.slice(0, 4));
    }
    if (userProfile.yearsExperience) setYearsExperience(userProfile.yearsExperience);
    if (userProfile.markets?.length) setMarkets(userProfile.markets);
    if (userProfile.languages?.length) setLanguages(userProfile.languages);
    if (userProfile.linkedinUrl) setLinkedinUrl(userProfile.linkedinUrl);
    if (userProfile.referralSource) setReferralSource(userProfile.referralSource);
    if (userProfile.bio) setNotes(userProfile.bio);
  }, [userProfile]);

  const toggleMarket = (m: string) =>
    setMarkets((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));

  const toggleLanguage = (l: string) =>
    setLanguages((prev) => (prev.includes(l) ? prev.filter((x) => x !== l) : [...prev, l]));

  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [showMarketDropdown, setShowMarketDropdown] = useState(false);

  const handleSave = async () => {
    try {
      await updateUserProfile({
        dreNumber: dreNumber || undefined,
        dreExpiry:
          dreExpiryMonth && dreExpiryDay && dreExpiryYear
            ? `${dreExpiryYear}-${dreExpiryMonth}-${dreExpiryDay}`
            : undefined,
        yearsExperience: yearsExperience || undefined,
        markets: markets.length ? markets : undefined,
        languages: languages.length ? languages : undefined,
        linkedinUrl: linkedinUrl || undefined,
        referralSource: referralSource || undefined,
        bio: notes || undefined,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save advisor details:', err);
    }
  };

  const availableMarkets = ALL_MARKETS.filter((m) => !markets.includes(m));

  return (
    <div>
      <SectionHeader title="Details" mobile={mobile} />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          maxWidth: mobile ? undefined : 560,
        }}>
        {/* DRE + Expiry / Years + Referral grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: mobile ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) minmax(0, 1fr)',
            columnGap: 12,
            rowGap: 20,
          }}>
          <div>
            <FieldLabel>DRE license #</FieldLabel>
            <input
              value={dreNumber}
              onChange={(e) => setDreNumber(e.target.value)}
              placeholder="e.g. 01234567"
              style={inputStyle}
              onFocus={(e) => {
                e.target.style.borderColor = C.lime;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = C.border;
              }}
            />
          </div>
          <div>
            <FieldLabel>DRE expiry</FieldLabel>
            {(() => {
              const daysInMonth =
                dreExpiryMonth && dreExpiryYear
                  ? new Date(Number(dreExpiryYear), Number(dreExpiryMonth), 0).getDate()
                  : 31;
              const dayOptions = Array.from({ length: daysInMonth }, (_, i) =>
                String(i + 1).padStart(2, '0'),
              );
              return (
                <div style={{ display: 'flex', gap: 8, width: '100%' }}>
                  <Autocomplete
                    sx={{ flex: 1, minWidth: 0 }}
                    disableClearable
                    options={EXPIRY_MONTHS}
                    value={dreExpiryMonth || ''}
                    onChange={(_, val) => setDreExpiryMonth(val ?? '')}
                    ListboxProps={{ style: { maxHeight: 180 } }}
                    slotProps={{
                      popper: {
                        placement: 'bottom-start',
                        modifiers: [{ name: 'flip', enabled: false }],
                      },
                      paper: { sx: dreDropdownPaperSx },
                    }}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="MM" sx={dreFieldSx} />
                    )}
                  />
                  <Autocomplete
                    sx={{ flex: 1, minWidth: 0 }}
                    disableClearable
                    options={dayOptions}
                    value={dreExpiryDay || ''}
                    onChange={(_, val) => setDreExpiryDay(val ?? '')}
                    ListboxProps={{ style: { maxHeight: 180 } }}
                    slotProps={{
                      popper: {
                        placement: 'bottom-start',
                        modifiers: [{ name: 'flip', enabled: false }],
                      },
                      paper: { sx: dreDropdownPaperSx },
                    }}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="DD" sx={dreFieldSx} />
                    )}
                  />
                  <Autocomplete
                    sx={{ flex: 1, minWidth: 0 }}
                    disableClearable
                    options={EXPIRY_YEARS.map(String)}
                    value={dreExpiryYear || ''}
                    onChange={(_, val) => setDreExpiryYear(val ?? '')}
                    ListboxProps={{ style: { maxHeight: 180 } }}
                    slotProps={{
                      popper: {
                        placement: 'bottom-start',
                        modifiers: [{ name: 'flip', enabled: false }],
                      },
                      paper: { sx: dreDropdownPaperSx },
                    }}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="YYYY" sx={dreFieldSx} />
                    )}
                  />
                </div>
              );
            })()}
          </div>

          <div>
            <FieldLabel>Experience</FieldLabel>
            <select
              value={yearsExperience}
              onChange={(e) => setYearsExperience(e.target.value)}
              style={selectStyle}>
              <option value="" style={{ color: 'var(--tx-muted)' }}>
                Select
              </option>
              {YEARS_OF_EXPERIENCE.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <div>
            <FieldLabel>Referral source</FieldLabel>
            <select
              value={referralSource}
              onChange={(e) => setReferralSource(e.target.value)}
              style={selectStyle}>
              <option value="" style={{ color: 'var(--tx-muted)' }}>
                Select source
              </option>
              {REFERRAL_SOURCES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: C.border, margin: '4px 0' }} />

        {/* Markets */}
        <div>
          <FieldLabel>Markets</FieldLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {POPULAR_MARKETS.map((m) => {
              const sel = markets.includes(m);
              return (
                <Chip
                  key={m}
                  label={m}
                  onClick={() => toggleMarket(m)}
                  sx={{
                    fontFamily: font,
                    fontSize: 11,
                    fontWeight: sel ? 600 : 500,
                    borderRadius: '6px',
                    height: 'auto',
                    py: '4px',
                    px: '10px',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    backgroundColor: sel ? 'var(--tx-fill-2)' : 'var(--tx-fill)',
                    color: sel ? 'var(--tx-text)' : 'var(--tx-muted)',
                    border: 0,
                    '& .MuiChip-label': { px: 0 },
                    '&:hover': {
                      backgroundColor: sel ? 'var(--tx-fill-2)' : 'var(--tx-fill-2)',
                    },
                  }}
                />
              );
            })}
            {/* Custom market chips (non-popular selections) */}
            {markets
              .filter((m) => !POPULAR_MARKETS.includes(m))
              .map((m) => (
                <Chip
                  key={m}
                  label={m}
                  onDelete={() => toggleMarket(m)}
                  sx={{
                    fontFamily: font,
                    fontSize: 11,
                    fontWeight: 600,
                    borderRadius: '6px',
                    height: 'auto',
                    py: '4px',
                    px: '10px',
                    backgroundColor: 'var(--tx-fill-2)',
                    color: 'var(--tx-text)',
                    border: 0,
                    '& .MuiChip-label': { px: 0 },
                    '& .MuiChip-deleteIcon': { color: 'var(--tx-muted)', fontSize: 14, ml: '6px' },
                    '& .MuiChip-deleteIcon:hover': { color: C.text },
                  }}
                />
              ))}
            {/* "More options" toggle */}
            {!showMarketDropdown && (
              <Chip
                label="+ More options"
                onClick={() => setShowMarketDropdown(true)}
                sx={{
                  fontFamily: font,
                  fontSize: 11,
                  fontWeight: 600,
                  borderRadius: '6px',
                  height: 'auto',
                  py: '4px',
                  px: '10px',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  backgroundColor: 'var(--tx-fill)',
                  color: 'var(--tx-muted)',
                  border: '1px dashed var(--tx-line)',
                  '& .MuiChip-label': { px: 0 },
                  '&:hover': {
                    backgroundColor: 'var(--tx-fill-2)',
                    borderColor: 'var(--tx-line)',
                  },
                }}
              />
            )}
          </div>
          {showMarketDropdown && (
            <Autocomplete
              open
              options={availableMarkets}
              onChange={(_, val) => {
                if (val && !markets.includes(val)) {
                  setMarkets((prev) => [...prev, val]);
                }
                setShowMarketDropdown(false);
              }}
              onClose={() => setShowMarketDropdown(false)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search CA counties..."
                  autoFocus
                  sx={{ ...muiInputSx, mt: 1.5 }}
                />
              )}
              slotProps={{
                paper: {
                  sx: {
                    // a menu surface is the solid popover (Tradehaus .tx-pop), never the 5% tile tint that lets the page show through
                    backgroundColor: 'var(--tx-dialog)',
                    backgroundImage: 'var(--tx-surface-light)',
                    boxShadow: 'var(--tx-shadow-sm)',
                    borderRadius: '12px',
                    mt: '4px',
                    '& .MuiAutocomplete-option': {
                      fontFamily: font,
                      fontSize: 13,
                      color: C.text,
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
                      '&[aria-selected="true"]': {
                        backgroundColor: 'var(--tx-fill-2)',
                      },
                    },
                  },
                },
              }}
            />
          )}
        </div>

        {/* Languages */}
        <div>
          <FieldLabel>Languages</FieldLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {LANGUAGES.filter((l) => l !== 'Other').map((l) => {
              const sel = languages.includes(l);
              return (
                <Chip
                  key={l}
                  label={l}
                  onClick={() => toggleLanguage(l)}
                  sx={{
                    fontFamily: font,
                    fontSize: 11,
                    fontWeight: sel ? 600 : 500,
                    borderRadius: '6px',
                    height: 'auto',
                    py: '4px',
                    px: '10px',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    backgroundColor: sel ? 'var(--tx-fill-2)' : 'var(--tx-fill)',
                    color: sel ? 'var(--tx-text)' : 'var(--tx-muted)',
                    border: 0,
                    '& .MuiChip-label': { px: 0 },
                    '&:hover': {
                      backgroundColor: sel ? 'var(--tx-fill-2)' : 'var(--tx-fill-2)',
                    },
                  }}
                />
              );
            })}
            {/* Custom language chips */}
            {languages
              .filter((l) => !LANGUAGES.includes(l))
              .map((l) => (
                <Chip
                  key={l}
                  label={l}
                  onDelete={() => toggleLanguage(l)}
                  sx={{
                    fontFamily: font,
                    fontSize: 11,
                    fontWeight: 600,
                    borderRadius: '6px',
                    height: 'auto',
                    py: '4px',
                    px: '10px',
                    backgroundColor: 'var(--tx-fill-2)',
                    color: 'var(--tx-text)',
                    border: 0,
                    '& .MuiChip-label': { px: 0 },
                    '& .MuiChip-deleteIcon': { color: 'var(--tx-muted)', fontSize: 14, ml: '6px' },
                    '& .MuiChip-deleteIcon:hover': { color: C.text },
                  }}
                />
              ))}
            {/* "More options" toggle */}
            {!showLangDropdown && (
              <Chip
                label="+ More options"
                onClick={() => setShowLangDropdown(true)}
                sx={{
                  fontFamily: font,
                  fontSize: 11,
                  fontWeight: 600,
                  borderRadius: '6px',
                  height: 'auto',
                  py: '4px',
                  px: '10px',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  backgroundColor: 'var(--tx-fill)',
                  color: 'var(--tx-muted)',
                  border: '1px dashed var(--tx-line)',
                  '& .MuiChip-label': { px: 0 },
                  '&:hover': {
                    backgroundColor: 'var(--tx-fill-2)',
                    borderColor: 'var(--tx-line)',
                  },
                }}
              />
            )}
          </div>
          {showLangDropdown && (
            <Autocomplete
              open
              options={MORE_LANGUAGES.filter((l) => !languages.includes(l))}
              onChange={(_, val) => {
                if (val && !languages.includes(val)) {
                  setLanguages((prev) => [...prev, val]);
                }
                setShowLangDropdown(false);
              }}
              onClose={() => setShowLangDropdown(false)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search languages..."
                  autoFocus
                  sx={{ ...muiInputSx, mt: 1.5 }}
                />
              )}
              slotProps={{
                paper: {
                  sx: {
                    // a menu surface is the solid popover (Tradehaus .tx-pop), never the 5% tile tint that lets the page show through
                    backgroundColor: 'var(--tx-dialog)',
                    backgroundImage: 'var(--tx-surface-light)',
                    boxShadow: 'var(--tx-shadow-sm)',
                    borderRadius: '12px',
                    mt: '4px',
                    '& .MuiAutocomplete-option': {
                      fontFamily: font,
                      fontSize: 13,
                      color: C.text,
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
                      '&[aria-selected="true"]': {
                        backgroundColor: 'var(--tx-fill-2)',
                      },
                    },
                  },
                },
              }}
            />
          )}
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: C.border, margin: '4px 0' }} />

        {/* LinkedIn */}
        <div>
          <FieldLabel>LinkedIn profile</FieldLabel>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              background: 'var(--tx-fill)',
              border: 0,
              borderRadius: 10,
              overflow: 'hidden',
            }}>
            <span
              style={{
                padding: '10px 0 10px 14px',
                color: C.muted,
                fontSize: 13,
                fontFamily: font,
                whiteSpace: 'nowrap',
              }}>
              linkedin.com/in/
            </span>
            <input
              value={linkedinUrl}
              onChange={(e) => {
                const v = e.target.value
                  .replace(/^https?:\/\/(www\.)?linkedin\.com\/in\//i, '')
                  .replace(/\/+$/, '');
                setLinkedinUrl(v);
              }}
              placeholder="yourname"
              style={{
                ...inputStyle,
                border: 'none',
                background: 'transparent',
                paddingLeft: 0,
                borderRadius: 0,
              }}
              onFocus={(e) => {
                (e.target.parentElement as HTMLElement).style.borderColor = C.lime;
              }}
              onBlur={(e) => {
                (e.target.parentElement as HTMLElement).style.borderColor = C.border;
              }}
            />
          </div>
        </div>

        {/* Bio */}
        <div>
          <FieldLabel>Bio</FieldLabel>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Tell clients a bit about yourself..."
            rows={3}
            style={{
              ...inputStyle,
              resize: 'vertical',
              minHeight: 80,
            }}
            onFocus={(e) => {
              e.target.style.borderColor = C.lime;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = C.border;
            }}
          />
        </div>
      </div>

      <button
        onClick={handleSave}
        style={{
          ...btnPrimary,
          width: mobile ? '100%' : 180,
          marginTop: 24,
          ...(saved ? { background: 'var(--tx-fill)', color: C.muted } : null),
        }}>
        {saved ? '\u2713 Saved' : 'Save details'}
      </button>
    </div>
  );
};

// ── Tabs ────────────────────────────────────────────────────────────────────

const BASE_TABS = [
  {
    id: 'profile',
    label: 'Profile',
    icon: (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <circle cx="12" cy="8" r="4" />
        <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
      </svg>
    ),
  },
  {
    id: 'users',
    label: 'Manage Users',
    icon: (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M19 8v6" />
        <path d="M22 11h-6" />
      </svg>
    ),
  },
  // {
  //   id: 'notifications',
  //   label: 'Notifications',
  //   icon: (
  //     <svg
  //       width="15"
  //       height="15"
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //       strokeLinejoin="round">
  //       <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
  //       <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  //     </svg>
  //   ),
  // },
];

const ADVISOR_TAB = {
  id: 'advisor',
  label: 'Details',
  icon: (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round">
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <circle cx="8" cy="12" r="2" />
      <path d="M14 10h4" />
      <path d="M14 14h4" />
    </svg>
  ),
};

export const SettingsView = () => {
  const [tab, setTab] = useState('profile');
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAdvisor, canAccessAdmin, isSuperAdmin } = useUserRole();

  const TABS = isSuperAdmin
    ? [BASE_TABS[0]]
    : isAdvisor || canAccessAdmin
    ? [BASE_TABS[0], ADVISOR_TAB, ...BASE_TABS.slice(1).filter((t) => t.id !== 'users')]
    : BASE_TABS;

  return (
    <Box
      data-testid="settings-view"
      sx={{
        background: 'transparent',
        fontFamily: font,
        // the frame's 20px gutter above and below, matching the sidebar island, so the sheet sits exactly within it
        padding: isMobile ? '10px 12px 20px' : '20px 0',
        // Compensates the fixed mobile nav's own spacing so the card sits
        // flush under it, same technique InsightsView.tsx already uses.
        mt: isMobile ? '-10px' : 0,
      }}>
      <Box>

        <Box
          sx={{
            display: isMobile ? 'flex' : 'grid',
            flexDirection: isMobile ? 'column' : undefined,
            gridTemplateColumns: isMobile ? undefined : 'clamp(200px, 18%, 240px) 1px 1fr',
            background: C.card,
            backgroundImage: 'var(--tx-surface-light)',
            boxShadow: 'var(--tx-shadow)',
            // Same 25px radius as every other card (see the `card()` helper
            // in HomeownerHub.tsx) — mobile previously used a smaller 16px.
            borderRadius: '24px',
            // a computer: the sheet is the viewport less the frame gutters, like the sidebar island; only its content scrolls
            height: isMobile ? undefined : 'calc(100dvh - 40px)',
            overflow: isMobile ? undefined : 'hidden',
            boxSizing: 'border-box',
            position: 'relative',
          }}>
          {/* Close (X): the ds sheet close, top right of the card, back to wherever settings was opened from */}
          <CloseButton
            onClose={() => navigate(-1)}
            label="Close settings"
            sx={{ position: 'absolute', top: isMobile ? 12 : 16, right: isMobile ? 12 : 16, zIndex: 1 }}
          />

          {isMobile ? (
            <>
              {/* Icon-only tab pills */}
              <Box
                sx={{
                  display: 'flex',
                  gap: '14px',
                  padding: '16px 16px 0',
                  justifyContent: 'flex-start',
                  flexShrink: 0,
                }}>
                {TABS.map((t) => (
                  <Tooltip key={t.id} title={t.label} placement="bottom">
                    <Box
                      onClick={() => setTab(t.id)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 44,
                        height: 44,
                        borderRadius: '10px',
                        cursor: 'pointer',
                        color: tab === t.id ? C.text : C.muted,
                        background: tab === t.id ? 'var(--tx-fill)' : 'transparent',
                        transition: 'all 0.15s',
                        '& svg': { width: 22, height: 22 },
                      }}>
                      {t.icon}
                    </Box>
                  </Tooltip>
                ))}
              </Box>

              {/* Horizontal divider */}
              <Box sx={{ height: 1, background: C.border, mx: '16px', mt: '12px' }} />

              {/* Content panel */}
              <Box sx={{ padding: '20px', overflow: 'auto' }}>
                {tab === 'profile' && <ProfileTab />}
                {tab === 'advisor' && <AdvisorTab />}
                {tab === 'users' && <ManageUsersTab />}
                {/* {tab === 'notifications' && <_NotificationsTab />} */}
              </Box>
            </>
          ) : (
            <>
              {/* Sidebar nav: the sheet's title, then the sections as the app's flat NavItem tiles */}
              <Box sx={{ p: '20px 12px' }}>
                <Box
                  sx={{
                    px: '8px',
                    mb: '16px',
                    fontSize: 17,
                    fontWeight: 400,
                    letterSpacing: '-0.015em',
                    color: C.text,
                    fontFamily: font,
                  }}>
                  Settings
                </Box>
                {TABS.map((t) => (
                  <Box
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      height: 36,
                      px: '10px',
                      mb: '2px',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: tab === t.id ? 600 : 500,
                      color: tab === t.id ? C.text : C.muted,
                      background: tab === t.id ? 'var(--tx-fill-2)' : 'transparent',
                      transition: 'color 0.15s, background 0.15s',
                      fontFamily: font,
                      '& svg': { width: 16, height: 16 },
                      '&:hover': tab !== t.id ? { color: C.text } : {},
                    }}>
                    {t.icon}
                    {t.label}
                  </Box>
                ))}
              </Box>

              {/* Vertical divider */}
              <Box sx={{ background: C.border }} />

              {/* Content panel */}
              <Box
                sx={{
                  padding: '24px 32px 32px',
                  overflowY: 'auto',
                  minHeight: 0,
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                }}>
                {tab === 'profile' && <ProfileTab />}
                {tab === 'advisor' && <AdvisorTab />}
                {tab === 'users' && <ManageUsersTab />}
                {/* {tab === 'notifications' && <_NotificationsTab />} */}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};
